import React, { useState } from 'react';
import { DollarSign, CreditCard, Download, CheckCircle, Clock, AlertCircle, Calendar, Receipt, FileText } from 'lucide-react';

const feeStructure = [
  { name: 'Tuition Fee', amount: 5000, frequency: 'Quarterly' },
  { name: 'Lab Fee', amount: 500, frequency: 'Quarterly' },
  { name: 'Library Fee', amount: 200, frequency: 'Quarterly' },
  { name: 'Sports Fee', amount: 300, frequency: 'Quarterly' },
  { name: 'Computer Lab Fee', amount: 400, frequency: 'Quarterly' },
  { name: 'Activity Fee', amount: 250, frequency: 'Quarterly' },
];

const paymentHistory = [
  {
    id: 'TXN001',
    date: 'Oct 15, 2025',
    description: 'Q3 2025 - Tuition & Fees',
    amount: 6650,
    status: 'paid',
    receiptNo: 'RCP-2025-0345',
    paymentMethod: 'Online Payment',
  },
  {
    id: 'TXN002',
    date: 'Jul 10, 2025',
    description: 'Q2 2025 - Tuition & Fees',
    amount: 6650,
    status: 'paid',
    receiptNo: 'RCP-2025-0234',
    paymentMethod: 'Bank Transfer',
  },
  {
    id: 'TXN003',
    date: 'Apr 05, 2025',
    description: 'Q1 2025 - Tuition & Fees',
    amount: 6650,
    status: 'paid',
    receiptNo: 'RCP-2025-0123',
    paymentMethod: 'Online Payment',
  },
  {
    id: 'TXN004',
    date: 'Jan 08, 2025',
    description: 'Annual Registration Fee',
    amount: 2000,
    status: 'paid',
    receiptNo: 'RCP-2025-0012',
    paymentMethod: 'Cash',
  },
];

const pendingPayments = [
  {
    id: 'PEN001',
    description: 'Q4 2025 - Tuition & Fees',
    amount: 6650,
    dueDate: 'Jan 31, 2026',
    status: 'pending',
    lateFee: 0,
  },
  {
    id: 'PEN002',
    description: 'Annual Sports Day Fee',
    amount: 500,
    dueDate: 'Feb 05, 2026',
    status: 'upcoming',
    lateFee: 0,
  },
];

const Fees = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const totalPaid = paymentHistory.reduce((sum, payment) => sum + payment.amount, 0);
  const totalPending = pendingPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const quarterlyTotal = feeStructure.reduce((sum, fee) => sum + fee.amount, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fee Details</h1>
          <p className="text-gray-600 mt-1">View and manage your fee payments</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
          <CreditCard size={18} />
          <span>Pay Now</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Total Paid (This Year)</p>
              <p className="text-3xl font-bold mt-2">${totalPaid.toLocaleString()}</p>
            </div>
            <CheckCircle size={40} className="text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Pending Amount</p>
              <p className="text-3xl font-bold mt-2">${totalPending.toLocaleString()}</p>
            </div>
            <Clock size={40} className="text-orange-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Quarterly Fee</p>
              <p className="text-3xl font-bold mt-2">${quarterlyTotal.toLocaleString()}</p>
            </div>
            <DollarSign size={40} className="text-blue-200" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {['overview', 'history', 'structure'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Pending Payments */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-orange-50">
              <div className="flex items-center gap-3">
                <AlertCircle size={20} className="text-orange-600" />
                <h2 className="text-lg font-semibold text-gray-900">Pending Payments</h2>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {pendingPayments.map((payment) => (
                <div key={payment.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900">{payment.description}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          <span>Due: {payment.dueDate}</span>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          payment.status === 'pending' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {payment.status === 'pending' ? 'Due Soon' : 'Upcoming'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-xl font-bold text-gray-900">${payment.amount.toLocaleString()}</p>
                      <button className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
                        Pay Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Download size={20} className="text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Download Receipts</p>
                <p className="text-sm text-gray-500">Get all payment receipts</p>
              </div>
            </button>
            <button className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText size={20} className="text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Fee Statement</p>
                <p className="text-sm text-gray-500">View detailed statement</p>
              </div>
            </button>
            <button className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <CreditCard size={20} className="text-purple-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Payment Methods</p>
                <p className="text-sm text-gray-500">Manage payment options</p>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Payment History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Date</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Description</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Receipt No.</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Method</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">Amount</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paymentHistory.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-600">{payment.date}</td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">{payment.description}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{payment.receiptNo}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{payment.paymentMethod}</td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">${payment.amount.toLocaleString()}</td>
                    <td className="px-6 py-4 text-center">
                      <button className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                        <Download size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Structure Tab */}
      {activeTab === 'structure' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Fee Structure</h2>
            <p className="text-sm text-gray-500 mt-1">Academic Year 2025-2026</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Fee Type</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600">Frequency</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {feeStructure.map((fee, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">{fee.name}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                        {fee.frequency}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">${fee.amount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-primary-50">
                <tr>
                  <td className="px-6 py-4 font-semibold text-primary-900">Total (Per Quarter)</td>
                  <td className="px-6 py-4"></td>
                  <td className="px-6 py-4 text-right font-bold text-primary-900 text-lg">${quarterlyTotal.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="p-6 bg-gray-50 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> Fees are payable at the beginning of each quarter. Late payment may attract a fine of $50 per week.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fees;
